import { motion } from "framer-motion";
import { EASE } from "@/lib/scroll";

const CHAPTERS = [
  {
    title: "The Heritage & Soul",
    body: "Portions of the cottage are over 100 years old: original shiplap, unique swing inward windows, and the kind of quiet only a century can earn. Inside, everything has been thoughtfully modernized for effortless summer living.",
    tags: "Est. 1922 · Modernized Interior",
  },
  {
    title: "The Car-Free Rhythm",
    body: "From July to Labour Day, the Victoria Beach Restricted Area belongs to bikes and bare feet. No streetlights, no traffic; kids ride and play like it's 1962 while parents actually relax. Mornings often start with a pedal to the Village Green Bakery, where the completely renovated little shop turns out fresh bread and treats every single day. Parents and grandparents now bring their little ones down for the exact same ritual they loved as kids.",
    tags: "The Restricted Area · Bikes & Bare Feet · Bakery Mornings",
  },
  {
    title: "Sunlit Gathering",
    body: "A private back deck built for long dinners: patio table, barbecue, and a secluded outdoor shower for rinsing off under the sun or the stars. After dark, twinkle lights in the trees and the screen room keep the evening going.",
    tags: "Private Deck · BBQ · Outdoor Shower",
  },
];

export default function Manifesto() {
  return (
    <section
      id="manifesto"
      data-testid="manifesto-section"
      className="mx-auto max-w-7xl px-6 py-24 sm:px-10 sm:py-36"
    >
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.9, ease: EASE }}
        className="mb-16 max-w-2xl"
      >
        <div className="mb-6 flex items-center gap-4">
          <span className="h-px w-12 bg-[#C8634B]" />
          <p className="font-mono text-[11px] uppercase tracking-[0.28em] text-[#C8634B]">
            The Manifesto
          </p>
        </div>
        <h2 className="font-serif text-3xl leading-snug tracking-tight text-[#1E3323] sm:text-4xl lg:text-5xl">
          What makes it{" "}
          <span className="italic text-[#C8634B]">special.</span>
        </h2>
      </motion.div>

      <div className="grid gap-12 md:grid-cols-3 md:gap-10">
        {CHAPTERS.map((chapter, i) => (
          <motion.article
            key={chapter.title}
            data-testid={`manifesto-chapter-${i + 1}`}
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.9, delay: i * 0.15, ease: EASE }}
            className="group border-t border-[#1E3323]/20 pt-8"
          >
            <span className="block h-0.5 w-8 bg-[#C8634B]" />
            <h3 className="mt-4 font-serif text-2xl leading-snug text-[#1E3323] transition-colors duration-300 group-hover:text-[#C8634B] sm:text-3xl">
              {chapter.title}
            </h3>
            <p className="mt-4 text-sm leading-relaxed text-[#4A4D46] sm:text-base">
              {chapter.body}
            </p>
            <p className="mt-6 font-mono text-[10px] uppercase tracking-[0.2em] text-[#767970]">
              {chapter.tags}
            </p>
          </motion.article>
        ))}
      </div>

      <motion.figure
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-60px" }}
        transition={{ duration: 0.9, ease: EASE }}
        className="mt-16"
      >
        <div className="rotate-[0.5deg] border border-[#1E3323]/15 bg-white p-3 shadow-lg shadow-[#1E3323]/5 transition-transform duration-500 hover:rotate-0">
          <img
            src="/photos/bike-gathering.webp"
            alt="Friends' bikes parked under the birches at the cottage"
            data-testid="manifesto-gathering-image"
            loading="lazy"
            className="max-h-[480px] w-full object-cover"
          />
        </div>
        <figcaption className="mt-4 text-center font-mono text-[10px] uppercase tracking-[0.2em] text-[#767970]">
          Common sight at parties in Victoria Beach
        </figcaption>
      </motion.figure>
    </section>
  );
}
